import { useEffect, useState } from 'react'
import type { StaffPromotion, StaffSegment, StaffPromotionInput } from '@unified-lk/api-contracts'
import { SegmentSelector } from './SegmentSelector'

interface Props {
  promotion: StaffPromotion | null  // null = create mode
  segments: StaffSegment[]
  segmentsLoading: boolean
  segmentsError: string | null
  onSave: (input: StaffPromotionInput) => Promise<void>
  onClose: () => void
}

const EMPTY: StaffPromotionInput = {
  title: '',
  description: '',
  content: '',
  image_url: '',
  button_text: 'Подробнее',
  button_url: '',
  start_date: '',
  end_date: '',
  priority: 0,
  is_active: true,
  segment_ids: [],
}

function promotionToInput(p: StaffPromotion): StaffPromotionInput {
  const segIds = p.segment_ids?.length
    ? p.segment_ids
    : (p.segments?.map((s) => s.id) ?? [])

  return {
    title: p.title,
    description: p.description ?? '',
    content: p.content ?? '',
    image_url: p.image_url ?? '',
    button_text: p.button_text ?? 'Подробнее',
    button_url: p.button_url ?? '',
    start_date: p.start_date ?? '',
    end_date: p.end_date ?? '',
    priority: p.priority ?? 0,
    is_active: p.is_active ?? true,
    segment_ids: segIds,
  }
}

export function PromotionFormModal({ promotion, segments, segmentsLoading, segmentsError, onSave, onClose }: Props) {
  const isEdit = promotion !== null
  const [form, setForm] = useState<StaffPromotionInput>(isEdit ? promotionToInput(promotion) : EMPTY)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setForm(isEdit ? promotionToInput(promotion) : EMPTY)
    setError(null)
  }, [promotion])

  function set<K extends keyof StaffPromotionInput>(key: K, value: StaffPromotionInput[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.title.trim()) {
      setError('Название обязательно')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await onSave(form)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка сохранения')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-8 backdrop-blur-sm">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-[28px] bg-white shadow-2xl">

        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-[28px] border-b border-slate-100 bg-gradient-to-r from-sky-50 to-indigo-50 px-6 py-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900">{isEdit ? 'Редактировать акцию' : 'Создать акцию'}</h2>
            <p className="text-xs text-slate-500">Акция будет видна пациентам из выбранных сегментов</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 text-slate-400 hover:bg-white hover:text-slate-700 transition">
            <i className="bi bi-x-lg text-lg" />
          </button>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="grid gap-5 p-6">

          {/* Title */}
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">
              Название <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder="Профессиональная чистка зубов"
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">Краткое описание (превью)</label>
            <textarea
              rows={2}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Краткое описание для карточки"
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
            />
          </div>

          {/* Full content */}
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">Полный текст акции</label>
            <textarea
              rows={4}
              value={form.content}
              onChange={(e) => set('content', e.target.value)}
              placeholder="Подробные условия..."
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
            />
          </div>

          {/* Image URL */}
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">URL изображения</label>
            <input
              type="text"
              value={form.image_url}
              onChange={(e) => set('image_url', e.target.value)}
              placeholder="https://... или /storage/..."
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
            />
          </div>

          {/* Button */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-700">Текст кнопки</label>
              <input
                type="text"
                value={form.button_text}
                onChange={(e) => set('button_text', e.target.value)}
                placeholder="Записаться"
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-700">Ссылка кнопки</label>
              <input
                type="text"
                value={form.button_url}
                onChange={(e) => set('button_url', e.target.value)}
                placeholder="https://..."
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
              />
            </div>
          </div>

          {/* Dates + Priority */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-700">Начало</label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => set('start_date', e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-sky-400"
              />
              <p className="mt-1 text-[11px] text-slate-400">Не указана = сразу</p>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-700">Конец</label>
              <input
                type="date"
                value={form.end_date}
                onChange={(e) => set('end_date', e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-sky-400"
              />
              <p className="mt-1 text-[11px] text-slate-400">Не указана = бессрочно</p>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-700">Приоритет</label>
              <input
                type="number"
                min={0}
                max={100}
                value={form.priority}
                onChange={(e) => set('priority', parseInt(e.target.value, 10) || 0)}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-sky-400"
              />
              <p className="mt-1 text-[11px] text-slate-400">0–100, выше = раньше</p>
            </div>
          </div>

          {/* Active toggle */}
          <label className="flex cursor-pointer items-center gap-3">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => set('is_active', e.target.checked)}
              className="h-4 w-4 rounded accent-sky-600"
            />
            <span className="text-sm font-semibold text-slate-700">Активна (показывать пациентам)</span>
          </label>

          {/* Segments */}
          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700">
              Целевые сегменты (до 3)
            </label>
            <SegmentSelector
              segments={segments}
              selected={form.segment_ids}
              onChange={(ids) => set('segment_ids', ids)}
              loading={segmentsLoading}
              error={segmentsError}
            />
          </div>

          {error && (
            <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-2xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-2xl bg-[linear-gradient(135deg,#0099ff_0%,#0077cc_100%)] px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-90 disabled:opacity-50 transition"
            >
              {saving ? 'Сохранение...' : isEdit ? 'Сохранить' : 'Создать'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
